import { useEffect, useState } from "react";
import {
  ApiError,
  fetchEvaluationSlots,
  fetchSlotDetail,
  type SlotDetailDto,
  type SlotSummaryDto,
} from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

const WINDOWS = [30, 60, 90] as const;
// Below this many RESOLVED observations, a live metric is noise, not a
// verdict (H3's sample-size-honesty rule) -- flagged consistently across
// Evaluation/ModelDetail/Dashboard rather than left to eyeball a bare n.
const SMALL_N_THRESHOLD = 30;

function fmtMetric(name: string | null, value: number | null): string {
  if (name === null || value === null) return "—";
  if (name === "IC" || name === "IC_sigma_rank") return `${value >= 0 ? "+" : ""}${value.toFixed(4)}`;
  if (name === "vol_error_qlike") return value.toFixed(3);
  return value.toFixed(4);
}

function metricLabel(name: string | null): string {
  if (name === "IC") return "rank-IC (return)";
  if (name === "IC_sigma_rank") return "rank-IC (sigma)";
  if (name === "vol_error_qlike") return "QLIKE";
  return name ?? "—";
}

function SmallNBadge({ n }: { n: number | null }) {
  if (n === null) return null;
  if (n >= SMALL_N_THRESHOLD) return null;
  return (
    <span className="gaps-badge" title="Few resolved predictions -- noisy, not a verdict. Do not read this as a confident conclusion.">
      small-n
    </span>
  );
}

function MetricWithN({ name, value, sampleN, pendingN }: { name: string | null; value: number | null; sampleN: number | null; pendingN: number | null }) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
      <span className="mono" style={{ fontWeight: 600 }}>{fmtMetric(name, value)}</span>
      <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint, #909ab0)" }}>
          n={sampleN ?? 0}<FieldHelp term="sample_n" />{pendingN !== null ? ` · ${pendingN} pending` : ""}
          {pendingN !== null && <FieldHelp term="pending_n" />}
        </span>
        <SmallNBadge n={sampleN} />
      </span>
    </span>
  );
}

/** V2 Live Forward Scorecard delta (userstories_scorecard.md US-D2) -- the
 * slot's own high-conviction bucket, so the historical (walk-forward) and
 * live (out-of-sample) views sit next to each other. Null for anything
 * that isn't a stock/return slot, or that the scorecard has no genuinely-
 * live data for yet -- rendered as an honest dash, never a fabricated
 * zero. */
function ScorecardDelta({ realizedIc, expectedIc, status }: { realizedIc: number | null; expectedIc: number | null; status: string | null }) {
  if (realizedIc === null && status === null) return null;
  const cls = status === "proven_live" ? "sc-flag-ok" : status === "underperforming" ? "sc-flag-bad" : "sc-flag-wait";
  return (
    <div className="note mono" style={{ fontSize: "0.68rem", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
      <FieldHelp term="forward_scorecard" />
      live: {realizedIc === null ? "—" : `${realizedIc >= 0 ? "+" : ""}${realizedIc.toFixed(4)}`}
      {expectedIc !== null && <span style={{ color: "var(--ink-faint)" }}>vs exp {expectedIc >= 0 ? "+" : ""}{expectedIc.toFixed(4)}</span>}
      {status && <span className={`sc-flag ${cls}`} style={{ padding: "1px 5px" }}>{status}</span>}
    </div>
  );
}

export function EvaluationPage({
  token,
  onUnauthorized,
  onSelectModel,
}: {
  token: string;
  onUnauthorized: () => void;
  onSelectModel: (modelId: string, version: string) => void;
}) {
  const [window_, setWindow] = useState<number>(60);
  const [slots, setSlots] = useState<SlotSummaryDto[] | null>(null);
  const { sorted: sortedSlots, sortKey, sortDir, toggleSort } = useSort<SlotSummaryDto>(
    slots ?? [],
    (row, key) => (row as unknown as Record<string, unknown>)[key],
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<SlotDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  useEffect(() => {
    setLoading(true);
    fetchEvaluationSlots(token, window_)
      .then(setSlots)
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "request failed");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, window_]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    setLoading(true);
    fetchSlotDetail(token, selected, window_)
      .then(setDetail)
      .catch((err) => {
        if (!handleAuthAwareError(err)) setError(err instanceof ApiError ? err.message : "request failed");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selected, window_]);

  if (selected) {
    return (
      <>
        <button className="btn ghost sm" onClick={() => setSelected(null)} style={{ marginBottom: 10 }}>
          ← All slots
        </button>
        {error && <div className="error" style={{ margin: 14 }}>{error}</div>}
        {!detail && loading && <div className="page-sub">Loading…</div>}
        {detail && <SlotDetailView detail={detail} window_={window_} onSetWindow={setWindow} onSelectModel={onSelectModel} />}
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Evaluation &amp; Champions</h1>
          <div className="page-sub">
            Champions are earned, not declared. Every model predicts daily; predictions are scored against reality
            after the horizon; the best live performer holds the slot.
          </div>
        </div>
        <div className="asof-control">
          <span className="note">trailing window:</span>
          <div style={{ display: "inline-flex", border: "1px solid var(--line2, #d9dee6)", borderRadius: 7, overflow: "hidden" }}>
            {WINDOWS.map((w) => (
              <button
                key={w}
                className={`app-nav-tab${window_ === w ? " active" : ""}`}
                style={{ padding: "5px 10px", fontSize: "0.72rem", borderRadius: 0 }}
                onClick={() => setWindow(w)}
              >
                {w}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <PageHelper>
        Every model registered to compete for a slot in production, with its <b>live-scored</b> performance —
        earned by tracking real outcomes, never hand-set. Click a slot to see its full contenders and the
        promotion gate.
      </PageHelper>

      <div className="banner show" style={{ display: "flex" }}>
        <span>ⓘ</span>
        <span>
          <b>Promotion rule (global):</b> a <FieldHelp term="champion_challenger" /> challenger takes the slot only
          when it (1) beats the champion on trailing live score, (2) by a statistically significant margin
          (<FieldHelp term="dm_test" /> DM test, p&lt;0.05), (3) with enough resolved predictions in{" "}
          <FieldHelp term="independent_blocks" /> <b>independent blocks</b> (≥50, block size = the slot's own{" "}
          <FieldHelp term="horizon" /> horizon). The rule is global; the comparison is per-slot. As of today{" "}
          <b>every one of the 47 slots has exactly one contender</b> — no slot has ever had a challenger, so this
          rule has not yet been exercised live on any of them.
        </span>
      </div>

      {error && <div className="error" style={{ margin: 14 }}>{error}</div>}

      <section className="results-panel">
        <div className="results-bar">
          <div className="results-left">
            <span className="results-count">
              <b>{slots ? slots.length : "…"}</b> slots
            </span>
          </div>
          <div className="results-tools">
            <button
              className="btn ghost sm"
              disabled={!slots || slots.length === 0}
              onClick={() =>
                exportRowsAsCsv(
                  sortedSlots as unknown as Record<string, unknown>[],
                  [
                    { key: "slot_key", label: "Slot" }, { key: "level", label: "Level" },
                    { key: "champion_model_id", label: "Champion" }, { key: "latest_metric_name", label: "Metric" },
                    { key: "latest_metric_value", label: "Value" }, { key: "sample_n", label: "Sample n" },
                    { key: "pending_n", label: "Pending n" }, { key: "monitored_not_raced", label: "Monitored not raced" },
                    { key: "n_contenders", label: "Contenders" },
                  ],
                  "evaluation_slots.csv",
                )
              }
            >
              ↓ CSV
            </button>
          </div>
        </div>
        {slots && (
          <div className="table-wrap">
            <table className="stocks-table">
              <thead>
                <tr>
                  <th className="l clickable" onClick={() => toggleSort("slot_key")}>Slot {sortGlyph("slot_key", sortKey, sortDir)}</th>
                  <th className="clickable" onClick={() => toggleSort("level")}>Level {sortGlyph("level", sortKey, sortDir)}</th>
                  <th className="clickable" onClick={() => toggleSort("champion_model_id")}>Champion {sortGlyph("champion_model_id", sortKey, sortDir)}</th>
                  <th className="r clickable" onClick={() => toggleSort("latest_metric_value")}>
                    Live metric <FieldHelp term="ic" /> {sortGlyph("latest_metric_value", sortKey, sortDir)}
                  </th>
                  <th>Evidence <FieldHelp term="evidence_speed" /></th>
                  <th>Monitoring <FieldHelp term="monitored_not_raced" /></th>
                  <th>Challenger?</th>
                </tr>
              </thead>
              <tbody>
                {sortedSlots.map((s) => (
                  <tr key={s.slot_key} className="clickable" onClick={() => setSelected(s.slot_key)} style={{ cursor: "pointer" }}>
                    <td className="l">
                      <span className="mono" style={{ fontSize: "0.8rem" }}>{s.slot_key}</span>
                      <div className="note" style={{ fontSize: "0.72rem" }}>{metricLabel(s.latest_metric_name)}</div>
                    </td>
                    <td><span className="chip">{s.level}</span></td>
                    <td className="mono" style={{ fontSize: "0.82rem" }}>{s.champion_model_id ?? "—"}</td>
                    <td className="r">
                      <MetricWithN name={s.latest_metric_name} value={s.latest_metric_value} sampleN={s.sample_n} pendingN={s.pending_n} />
                      <ScorecardDelta realizedIc={s.scorecard_realized_ic} expectedIc={s.scorecard_expected_ic} status={s.scorecard_status} />
                    </td>
                    <td>
                      {s.level === "stock" ? (
                        <span style={{ color: "var(--up, #0e9f6e)", fontFamily: "var(--font-mono)", fontSize: "0.72rem" }}>fast (segment)</span>
                      ) : (
                        <span style={{ color: "var(--down, #df453b)", fontFamily: "var(--font-mono)", fontSize: "0.72rem" }}>slow (index)</span>
                      )}
                    </td>
                    <td>
                      {s.monitored_not_raced ? (
                        <span className="chip" title="Monitored (scored daily) but never raced -- persistence has no fitted alternative to compete against.">
                          monitored, not raced
                        </span>
                      ) : (
                        <span className="chip st-champion">raced</span>
                      )}
                    </td>
                    <td>{s.n_contenders > 1 ? <span className="chip st-challenger">{s.n_contenders - 1} challenger(s)</span> : <span className="note">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="foot-note">
          Cross-sectional <b>segment</b> slots accumulate evidence fast (each day scores every constituent → thousands
          of predictions). <b>Index</b> slots accumulate slowly (one prediction/day) — so an index champion's live
          metric carries far fewer resolved observations for a long time. Click a slot for its contenders and gate.
        </div>
      </section>
    </>
  );
}

function verdictClass(status: string): string {
  if (status === "promotable") return "v-go";
  if (status === "no_challenger") return "v-no";
  return "v-wait";
}

function SlotDetailView({
  detail,
  window_,
  onSetWindow,
  onSelectModel,
}: {
  detail: SlotDetailDto;
  window_: number;
  onSetWindow: (w: number) => void;
  onSelectModel: (modelId: string, version: string) => void;
}) {
  const champ = detail.contenders.find((c) => c.is_champion);
  const challengers = detail.contenders.filter((c) => !c.is_champion);
  return (
    <>
      <div className="page-head">
        <div>
          <h1>{detail.slot_key}</h1>
          <div className="page-sub">
            {detail.level === "stock"
              ? `cross-sectional segment · scored daily across constituents → fast-accumulating evidence`
              : `per-instrument · 1 prediction/day → evidence accumulates slowly`}
            {detail.monitored_not_raced && (
              <>
                {" "}
                — <b>monitored, not raced</b>: this is a persistence-convention model with no fitted alternative to
                compete against, scored for accuracy but never a promotion candidate.
              </>
            )}
          </div>
        </div>
        <div className="asof-control">
          <span className="note">window:</span>
          <div style={{ display: "inline-flex", border: "1px solid var(--line2, #d9dee6)", borderRadius: 7, overflow: "hidden" }}>
            {WINDOWS.map((w) => (
              <button
                key={w}
                className={`app-nav-tab${window_ === w ? " active" : ""}`}
                style={{ padding: "5px 10px", fontSize: "0.72rem", borderRadius: 0 }}
                onClick={() => onSetWindow(w)}
              >
                {w}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="h3">
          <span>Contenders — live-scored <span className="note">predicted vs realized, tallied over the trailing window</span></span>
        </div>
        <div className="table-wrap">
          <table className="stocks-table">
            <thead>
              <tr>
                <th className="l">Contender</th>
                <th>Kind</th>
                <th className="r">Live metric</th>
                <th>Standing</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {detail.contenders.map((c) => (
                <tr key={c.model_id}>
                  <td className="l mono" style={{ fontWeight: 600 }}>{c.model_id}</td>
                  <td><span className="typ">{c.model_kind}</span></td>
                  <td className="r">
                    <MetricWithN name={c.metric_name} value={c.metric_value} sampleN={c.sample_n} pendingN={c.pending_n} />
                  </td>
                  <td>
                    <span className={`chip ${c.is_champion ? "st-champion" : "st-challenger"}`}>
                      {c.is_champion ? "champion" : "challenger"}
                    </span>
                  </td>
                  <td>
                    <button className="btn ghost sm" onClick={() => onSelectModel(c.model_id, c.version)}>
                      Model detail →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {champ && (
        <div className="card">
          <div className="h3"><span>Head-to-head gate <span className="note">champion vs leading challenger, on live evidence</span></span></div>
          {challengers.length === 0 ? (
            <div className="empty-state">
              <div className="headline">No challenger for this slot</div>
              <div>{detail.verdict.message}</div>
            </div>
          ) : (
            <>
              <div className="h2h" style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 16, alignItems: "center" }}>
                <div className="h2card champ" style={{ border: "1px solid #c9ebda", background: "var(--upSoft, #e4f5ee)", borderRadius: 10, padding: 14, textAlign: "center" }}>
                  <div className="note">Champion</div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800 }}>{champ.model_id}</div>
                  <div className="mono" style={{ fontSize: "1.4rem" }}>{fmtMetric(champ.metric_name, champ.metric_value)}</div>
                  <div className="note mono">n={champ.sample_n ?? 0}</div>
                </div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, color: "var(--ink-faint,#b6becd)" }}>vs</div>
                <div className="h2card chall" style={{ border: "1px solid var(--accentLine,#c9c8f2)", background: "var(--accentSoft,#ecebfb)", borderRadius: 10, padding: 14, textAlign: "center" }}>
                  <div className="note">Challenger</div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800 }}>{challengers[0].model_id}</div>
                  <div className="mono" style={{ fontSize: "1.4rem" }}>{fmtMetric(challengers[0].metric_name, challengers[0].metric_value)}</div>
                  <div className="note mono">n={challengers[0].sample_n ?? 0}</div>
                </div>
              </div>
              <div className={`verdict ${verdictClass(detail.verdict.status)}`} style={{ borderRadius: 9, padding: "13px 16px", marginTop: 14 }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800 }}>
                  {detail.verdict.status === "promotable" ? "Promotable — challenger has genuinely beaten the champion"
                    : detail.verdict.status === "insufficient_evidence" ? "Ahead, but not enough evidence yet"
                    : detail.verdict.status === "not_significant" ? "Ahead, but not statistically significant"
                    : "Challenger behind — no promotion"}
                </div>
                <div>{detail.verdict.message}</div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
